import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  TextField,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useBusinessProfileFilters from '../../resources/businessProfiles/components/BusinessProfilesFilterHelper';
import { useGetBusinessProfilesHandler } from '../../resources/businessProfiles/dataHandlers/getBusinessProfilesHandler';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';
import { isBusinessProfileComplete } from '../../constants/businessProfileStatusConstants.jsx';
import { CREDIT_NOTE_DOCUMENT_TYPE_CODE } from '../../constants/defaults';
import { isCommercialDocumentType } from '../../utils/documentTypeRules';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { READ_BUSINESS_PROFILE } from '../../constants/eInvoicePermissions';
import { useAccessDeniedMessage } from '../../hooks/useAccessDeniedMessage';
import PermissionDeniedWrapper from '../PermissionDeniedWrapper';
import { getFieldErrorMessage } from '../../utils/apiFieldErrors';

const getBusinessProfileOptionLabel = (option) => {
  const name = option?.party_name || option?.name || '';
  const email = option?.email || '';
  const identifier = option?.tin || option?.company_id || '';
  return [name, email, identifier].filter(Boolean).join(' | ');
};

const hasVatNumber = (profile) => {
  const vatNumber = profile?.party_tax_scheme?.company_id;
  return typeof vatNumber === 'string' && vatNumber.trim() !== '';
};

const BusinessProfileSelect = ({
  label,
  name,
  control,
  errors,
  readOnly = false,
  onProfilesLoaded,
  invoiceTypeCode,
}) => {
  const navigate = useNavigate();
  const [incompleteModalOpen, setIncompleteModalOpen] = useState(false);
  const [pendingIncompleteProfile, setPendingIncompleteProfile] = useState(null);
  const { loading: permissionsLoading, hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage('business_profiles_read_denied');
  const canReadBusinessProfiles = hasEInvoicePermission(READ_BUSINESS_PROFILE);
  const isPermissionDenied = !permissionsLoading && !canReadBusinessProfiles;
  const isSelectDisabled = isPermissionDenied;
  const { common, validations } = useTranslatedMessages();
  const {
    label: selectLabel,
    incomplete_modal_title,
    incomplete_modal_message,
    incomplete_modal_cancel,
    incomplete_modal_update,
  } = common.business_profile_select ?? {};
  const resolvedLabel = label ?? selectLabel;
  const requiredVatForCommercialMessage =
    invoiceTypeCode === CREDIT_NOTE_DOCUMENT_TYPE_CODE
      ? (validations?.credit_note?.billed_by_vat_required_for_commercial ??
        'Commercial credit note (381) requires billed-by profile with VAT number')
      : (validations?.invoice?.billed_by_vat_required_for_commercial ??
        'Commercial invoice (380) requires billed-by profile with VAT number');
  const errorMessage = getFieldErrorMessage(errors, name);

  const getDisabledReason = (profile) => {
    if (isCommercialDocumentType(invoiceTypeCode) && !hasVatNumber(profile)) {
      return requiredVatForCommercialMessage;
    }

    return '';
  };
  const { currentQuery, handleSearchChange } = useBusinessProfileFilters({
    defaultQuery: {},
    searchOnChange: true,
  });

  const { businessProfiles, isLoading } = useGetBusinessProfilesHandler({
    searchQuery: currentQuery,
    enabled: !permissionsLoading && canReadBusinessProfiles,
  });

  useEffect(() => {
    if (businessProfiles) {
      onProfilesLoaded?.(businessProfiles);
    }
  }, [businessProfiles, onProfilesLoaded]);

  const closeIncompleteModal = () => {
    setIncompleteModalOpen(false);
    setPendingIncompleteProfile(null);
  };

  const goToUpdateBusinessProfile = () => {
    const profileId = pendingIncompleteProfile?.profile_id;
    closeIncompleteModal();
    if (profileId) {
      navigate(`/business-profiles/${profileId}/edit`);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box display="flex" alignItems="flex-start" gap={2}>
          <PermissionDeniedWrapper show={isPermissionDenied} title={accessDeniedMessage} fullWidth>
            <Autocomplete
              loading={isLoading}
              id={name}
              fullWidth
              readOnly={readOnly}
              disabled={isSelectDisabled}
              options={businessProfiles}
              getOptionLabel={getBusinessProfileOptionLabel}
              getOptionKey={(option) => option?.id}
              getOptionDisabled={(option) => Boolean(getDisabledReason(option))}
              renderOption={(props, option) => {
                const { key, onClick, onMouseDown, ...optionProps } = props;
                const incomplete = !isBusinessProfileComplete(option);
                const disabledReason = getDisabledReason(option);
                const blockDisabledSelection = (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                };
                const optionLabel = (
                  <>
                    {incomplete && (
                      <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0 }} aria-hidden />
                    )}{' '}
                    {getBusinessProfileOptionLabel(option)}
                  </>
                );

                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                    onMouseDown={disabledReason ? blockDisabledSelection : onMouseDown}
                    onClick={disabledReason ? blockDisabledSelection : onClick}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      opacity: disabledReason ? 0.5 : 1,
                      cursor: disabledReason ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {disabledReason ? (
                      <Tooltip title={disabledReason} placement="right">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'auto',
                          }}
                        >
                          {optionLabel}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {optionLabel}
                      </Box>
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={resolvedLabel}
                  error={!!errorMessage}
                  helperText={errorMessage}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(_, value) => {
                if (value && getDisabledReason(value)) {
                  return;
                }

                if (value && !isBusinessProfileComplete(value)) {
                  setPendingIncompleteProfile(value);
                  setIncompleteModalOpen(true);
                  return;
                }
                field.onChange(value);
              }}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              value={field.value || null}
              onInputChange={(event, value, reason) => {
                if (reason === 'input') {
                  handleSearchChange(event);
                }
              }}
              filterOptions={(x) => x}
            />
          </PermissionDeniedWrapper>
          <Dialog open={incompleteModalOpen} onClose={closeIncompleteModal} maxWidth="sm" fullWidth>
            <DialogTitle>{incomplete_modal_title ?? 'Business Profile Incomplete'}</DialogTitle>
            <DialogContent>
              <Typography>
                {incomplete_modal_message ??
                  'Your business profile is incomplete. Update the business profile before you can start issuing invoices.'}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
              <Button variant="outlined" onClick={closeIncompleteModal}>
                {incomplete_modal_cancel ?? 'Cancel'}
              </Button>
              <Button variant="contained" onClick={goToUpdateBusinessProfile}>
                {incomplete_modal_update ?? 'Update Business Profile Now'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    />
  );
};

export default BusinessProfileSelect;
